import { Link } from "react-router-dom";
import { Clock, ChevronRight } from "lucide-react";

// Category-coloured accent (used sparingly for the chip on cards)
const CATEGORY_ACCENT = {
  "buying-guides":       "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  "product-comparisons": "bg-sky-500/10 text-sky-700 dark:text-sky-300",
  "amazon-tips":         "bg-amber-500/10 text-amber-700 dark:text-amber-300",
  "tech":                "bg-indigo-500/10 text-indigo-700 dark:text-indigo-300",
  "home-and-kitchen":    "bg-rose-500/10 text-rose-700 dark:text-rose-300",
  "diy":                 "bg-orange-500/10 text-orange-700 dark:text-orange-300",
  "gaming":              "bg-violet-500/10 text-violet-700 dark:text-violet-300",
  "health-and-fitness":  "bg-teal-500/10 text-teal-700 dark:text-teal-300",
  "smart-shopping":      "bg-cyan-500/10 text-cyan-700 dark:text-cyan-300",
  "gift-ideas":          "bg-pink-500/10 text-pink-700 dark:text-pink-300",
};

// Placeholder gradient shown when hero_image is missing / 404s.
// Deterministic-per-slug so re-renders don't flicker between colours.
const PLACEHOLDER_GRADIENTS = [
  "from-slate-800 via-slate-700 to-slate-900",
  "from-emerald-800 via-emerald-700 to-slate-900",
  "from-indigo-800 via-indigo-700 to-slate-900",
  "from-rose-800 via-rose-700 to-slate-900",
  "from-amber-800 via-amber-700 to-slate-900",
];
const hashSlug = (s = "") => {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
};
const placeholderFor = (slug) =>
  PLACEHOLDER_GRADIENTS[hashSlug(slug) % PLACEHOLDER_GRADIENTS.length];

export const CategoryChip = ({ category_slug, category_name, className = "" }) => (
  <span
    className={`inline-flex items-center px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider rounded-full ${
      CATEGORY_ACCENT[category_slug] || "bg-secondary text-muted-foreground"
    } ${className}`}
  >
    {category_name}
  </span>
);

export const ArticleCardHero = ({ article }) => (
  <Link
    to={`/buying-advice/${article.slug}`}
    data-testid={`article-card-hero-${article.slug}`}
    className="group grid md:grid-cols-2 gap-6 md:gap-10 items-center"
  >
    <div
      className={`aspect-[16/10] rounded-2xl overflow-hidden bg-gradient-to-br ${placeholderFor(
        article.slug
      )} relative`}
    >
      {article.hero_image ? (
        <img
          src={article.hero_image}
          alt={article.hero_image_alt || article.title}
          loading="eager"
          className="w-full h-full object-cover transition duration-500 group-hover:scale-[1.03]"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      ) : null}
      <span className="absolute top-4 left-4">
        <CategoryChip
          category_slug={article.category_slug}
          category_name={article.category_name}
          className="backdrop-blur bg-white/80 dark:bg-black/40 text-foreground"
        />
      </span>
    </div>
    <div>
      <div className="text-xs text-muted-foreground uppercase tracking-widest mb-3">
        Featured
      </div>
      <h2 className="text-3xl sm:text-4xl font-serif leading-tight mb-4 group-hover:text-primary transition">
        {article.title}
      </h2>
      <p className="text-base text-muted-foreground mb-4">{article.excerpt}</p>
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <span>{article.author}</span>
        <span>·</span>
        <span>{article.published_at}</span>
        <span>·</span>
        <span className="inline-flex items-center gap-1">
          <Clock className="w-3 h-3" /> {article.reading_time_min} min read
        </span>
      </div>
      <div className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary">
        Read the article
        <ChevronRight className="w-4 h-4 transition group-hover:translate-x-0.5" />
      </div>
    </div>
  </Link>
);

export const ArticleCard = ({ article }) => (
  <Link
    to={`/buying-advice/${article.slug}`}
    data-testid={`article-card-${article.slug}`}
    className="group block"
  >
    <div
      className={`aspect-[16/10] rounded-xl overflow-hidden bg-gradient-to-br ${placeholderFor(
        article.slug
      )} relative mb-4`}
    >
      {article.hero_image ? (
        <img
          src={article.hero_image}
          alt={article.hero_image_alt || article.title}
          loading="lazy"
          className="w-full h-full object-cover transition duration-500 group-hover:scale-[1.03]"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      ) : null}
      <span className="absolute top-3 left-3">
        <CategoryChip
          category_slug={article.category_slug}
          category_name={article.category_name}
          className="backdrop-blur bg-white/80 dark:bg-black/40 text-foreground"
        />
      </span>
    </div>
    <h3 className="text-lg font-serif leading-snug mb-2 group-hover:text-primary transition">
      {article.title}
    </h3>
    <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
      {article.excerpt}
    </p>
    <div className="flex items-center gap-2 text-[11px] text-muted-foreground uppercase tracking-widest">
      <span>{article.published_at}</span>
      <span>·</span>
      <span className="inline-flex items-center gap-1">
        <Clock className="w-3 h-3" /> {article.reading_time_min} min
      </span>
    </div>
  </Link>
);

export const ArticleCardCompact = ({ article }) => (
  <Link
    to={`/buying-advice/${article.slug}`}
    data-testid={`article-card-compact-${article.slug}`}
    className="group flex gap-4 items-start hover:bg-secondary/50 rounded-lg p-2 -m-2 transition"
  >
    <div
      className={`w-20 h-20 shrink-0 rounded-lg overflow-hidden bg-gradient-to-br ${placeholderFor(
        article.slug
      )}`}
    >
      {article.hero_image ? (
        <img
          src={article.hero_image}
          alt=""
          loading="lazy"
          className="w-full h-full object-cover"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      ) : null}
    </div>
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
        {article.category_name}
      </div>
      <h4 className="text-sm font-medium leading-snug group-hover:text-primary transition line-clamp-2">
        {article.title}
      </h4>
      <div className="text-[11px] text-muted-foreground mt-1">
        {article.reading_time_min} min read
      </div>
    </div>
  </Link>
);
